import * as DOMUtils from "history/DOMUtils";
import Debug from "../../../common-deprecated/Debug";
import { getToyotaCode } from "../../../common-deprecated/utils";
import { showModal } from "../../../common-deprecated/redux/actions/ModalActions";
import { getExternalPricePayload } from "../utils/externalPriceUtils";
import type { StockFinancePayload, UsedFinanceInsurancePayload } from "../utils/financeUtils";
import { getFinanceInsurancePayloadSelector } from "../utils/financeUtils";
import {
    initVehicle,
    setAggregatedVehicleCount,
    setRequireVehicleFetch,
    setVehicleFetchFailed,
    setVehiclePrice,
} from "../redux/actions/UscVehicleActions";
import { setCode } from "../redux/actions/UscSaveActions";
import { setInsuranceFailed, setInsuranceQuote, setInsuranceQuoteId } from "../redux/actions/UscInsuranceActions";
import { setFinanceFetchFailed, setFinanceInfo, setFinanceQuoteId } from "../redux/actions/UscFinanceActions";
import {
    setExternalPriceData,
    setExternalPriceFailed,
    setUserTaxLocation,
} from "../redux/actions/UscExternalPriceActions";
import { setCarDeliveryConfig } from "../redux/actions/UscCarDeliveryActions";
import { initDealer } from "../redux/actions/UscDealerActions";
import type { UscDispatchType, UscStateType } from "../redux/store";
import { showUscPriceSelector } from "../utils/priceUtils";
import type { FetchFnReturnType } from "../../../common-deprecated/utils/backendFetch";
import { initUscSettings } from "../redux/actions/UscSettingsActions";
import { COMPONENT_USED_STOCK_CARS_PDF } from "../../../shared-logic/server/components";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import { enableUscInsurance, enableUscMonthlyRate } from "../utils/settings";
import type { VehicleType } from "../../shared-logic/types/UscCommonTypes";
import { UscContext } from "../../shared-logic/types/UscCommonTypes";
import type { FetchUscAccessoriesReturnType } from "../server/fetchUscAccessories";
import { initUscAccessories, setUscAccessoriesFailed } from "../redux/actions/UscAccessoriesActions";
import type { EcoCostsPayloadType, EcoCostsType } from "../utils/ecoCostsUtils";
import { getEcoCostsPayload } from "../utils/ecoCostsUtils";
import { setEcoCostsData, setEcoCostsErrors, setEcoCostsFailed } from "../redux/actions/EcoCostsActions";
import type { UscEnv } from "../../shared-logic/utils/uscUtils";
import type { AggregatedVehicleCountReturnType } from "../server/fetchAggregatedVehicleCount";
import type { UscInsuranceQuoteType } from "../utils/insuranceUtils";
import type { ExternalPricePayloadType } from "../utils/externalPriceTypes";
import type { FetchTmefDataType } from "../server/fetchTmefData";
import type { DfVehicleStatus } from "../../shared-logic/utils/dfConstants";
import type { UsedFinanceInfoFetchType } from "../utils/types";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import type { UscLoadSaveCodeType } from "../utils/tyCodeUtils";
import type { VehicleFetchReturnType } from "../utils/constants";
import { UscFinanceDataSource } from "../utils/constants";
import { vehicleStatusQueryMap } from "../utils/helpers";
import { MODAL_USC_DEBUG } from "../utils/modalConstants";
import { enableLegalEcoLabel } from "../utils/pdpUtils";
import type { UscAccessoryPackParamType } from "../utils/accessoryUtils";
import { getUscAccessoryPackParams } from "../utils/accessoryUtils";

// ----------------------------------------------------------------------
// Fetch types
// ----------------------------------------------------------------------
export const FETCH_USC_SETTINGS = "fetchUscV2Settings";
export const FETCH_USC_ACCESSORIES = "fetchUscAccessories";
export const FETCH_USC_AGGREGATED_VEHICLE_COUNT = "fetchAggregatedVehicleCount";
export const FETCH_USC_VEHICLE = "fetchUscVehicle";
export const FETCH_USC_PREVIEW_VEHICLE = "fetchUscPreviewVehicle";
export const FETCH_USC_TYCODE = "fetchUscTyCode";
export const FETCH_USC_DEALER = "fetchUscDealer";
export const FETCH_USC_VEHICLE_PRICE = "fetchUscVehiclePrice";
export const FETCH_USC_INSURANCE_QUOTE = "fetchUscInsuranceQuote";
export const FETCH_EXT_PRICE_DATA = "fetchExtPriceData";
export const FETCH_USED_FINANCE_QUOTE = "fetchUsedFinanceQuote";
export const FETCH_STOCK_FINANCE_QUOTE = "fetchStockFinanceQuote";
export const FETCH_ECO_COSTS = "fetchEcoCosts";

export const fetchUscSettings = (context: UscContext): FetchUscSettingsType => ({
    fetchType: FETCH_USC_SETTINGS,
    context,
});

export const fetchUscAccessories = (params: UscAccessoryPackParamType): FetchUscAccessoriesType => ({
    fetchType: FETCH_USC_ACCESSORIES,
    params,
});

export const fetchAggregatedVehicleCount = (
    versionCode: string,
    context: UscContext,
    uscEnv: UscEnv,
): FetchAggregatedVehicleCountType => ({
    fetchType: FETCH_USC_AGGREGATED_VEHICLE_COUNT,
    versionCode,
    uscEnv,
    context,
});

export type FetchUscSettingsType = {
    fetchType: typeof FETCH_USC_SETTINGS;
    context: UscContext;
    result?: FetchFnReturnType<UscSettingsType>;
};

export type FetchUscAccessoriesType = {
    fetchType: typeof FETCH_USC_ACCESSORIES;
    params: UscAccessoryPackParamType;
    result?: FetchFnReturnType<FetchUscAccessoriesReturnType | null> | null;
};

export type FetchAggregatedVehicleCountType = {
    fetchType: typeof FETCH_USC_AGGREGATED_VEHICLE_COUNT;
    versionCode: string;
    uscEnv: UscEnv;
    context: UscContext;
    result?: FetchFnReturnType<AggregatedVehicleCountReturnType | null>;
};

export type VehicleOverridesType = { isHubCar?: boolean; status?: DfVehicleStatus; context?: UscContext };

export type FetchUscVehicleType = {
    fetchType: typeof FETCH_USC_VEHICLE;
    vehicleForSaleId: string;
    context: UscContext;
    uscEnv: UscEnv;
    waitForUncachedResult: boolean;
    addDebugData: boolean;
    // Overrides are used to (you guessed it) override certain properties of the vehicle after fetching.
    // Mostly relevant for debugging/layout purposes.
    overrides?: VehicleOverridesType;
    result?: FetchFnReturnType<VehicleFetchReturnType> | null;
};

export type FetchUscTyCodeType = {
    fetchType: typeof FETCH_USC_TYCODE;
    tyCode: string;
    saveGatewayUrl: string;
    waitForUncachedResult: boolean;
    result?: FetchFnReturnType<UscLoadSaveCodeType | null> | null;
};

export type FetchUscPreviewVehicleType = {
    fetchType: typeof FETCH_USC_PREVIEW_VEHICLE;
    context: UscContext;
    uscEnv: UscEnv;
    result?: FetchFnReturnType<VehicleFetchReturnType> | null;
};

export type FetchUscDealerType = {
    fetchType: typeof FETCH_USC_DEALER;
    dealerId: string;
    result?: FetchFnReturnType<DealerResultType | null> | null;
};

export type FetchUscVehiclePriceType = {
    fetchType: typeof FETCH_USC_VEHICLE_PRICE;
    vehicleForSaleId: string;
    context: UscContext;
    uscEnv: UscEnv;
    result?: FetchFnReturnType<VehicleType["price"]> | null;
};

export type FetchExternalPriceType = {
    fetchType: typeof FETCH_EXT_PRICE_DATA;
    payload: ExternalPricePayloadType;
    context: UscContext;
    uscEnv: UscEnv;
    result?: FetchFnReturnType<FetchTmefDataType> | null;
};

export type FetchUsedFinanceQuote = {
    fetchType: typeof FETCH_USED_FINANCE_QUOTE;
    payload: UsedFinanceInsurancePayload;
    context: UscContext;
    result?: FetchFnReturnType<UsedFinanceInfoFetchType> | null;
};

export type FetchStockFinanceQuote = {
    fetchType: typeof FETCH_STOCK_FINANCE_QUOTE;
    payload: StockFinancePayload;
    context: UscContext;
    result?: FetchFnReturnType<UsedFinanceInfoFetchType> | null;
};

export type FetchUscInsuranceQuote = {
    fetchType: typeof FETCH_USC_INSURANCE_QUOTE;
    quoteId: string;
    result?: FetchFnReturnType<UscInsuranceQuoteType> | null;
    uscEnv: UscEnv;
};

export type FetchEcoCostsType = {
    fetchType: typeof FETCH_ECO_COSTS;
    payload: EcoCostsPayloadType;
    uscEnv: UscEnv;
    result?: FetchFnReturnType<EcoCostsType> | null;
};

export const fetchUscVehicle = (
    vehicleForSaleId: string,
    context: UscContext,
    uscEnv: UscEnv,
    waitForUncachedResult: boolean = true,
    addDebugData: boolean = false,
    overrides?: VehicleOverridesType,
): FetchUscVehicleType => ({
    fetchType: FETCH_USC_VEHICLE,
    vehicleForSaleId,
    context,
    uscEnv,
    waitForUncachedResult,
    addDebugData,
    overrides,
});

export const fetchUscPreviewVehicle = (context: UscContext, uscEnv: UscEnv): FetchUscPreviewVehicleType => ({
    fetchType: FETCH_USC_PREVIEW_VEHICLE,
    context,
    uscEnv,
});

export const fetchUscTyCode = (
    tyCode: string,
    saveGatewayUrl: string,
    waitForUncachedResult: boolean = true,
): FetchUscTyCodeType => ({
    fetchType: FETCH_USC_TYCODE,
    tyCode,
    saveGatewayUrl,
    waitForUncachedResult,
});

export const fetchUscDealer = (dealerId: string): FetchUscDealerType => ({
    fetchType: FETCH_USC_DEALER,
    dealerId,
});

export const fetchUscVehiclePrice = (
    vehicleForSaleId: string,
    context: UscContext,
    uscEnv: UscEnv,
): FetchUscVehiclePriceType => ({
    fetchType: FETCH_USC_VEHICLE_PRICE,
    vehicleForSaleId,
    context,
    uscEnv,
});

export const fetchTmefData = (
    payload: ExternalPricePayloadType,
    context: UscContext,
    uscEnv: UscEnv,
): FetchExternalPriceType => ({
    fetchType: FETCH_EXT_PRICE_DATA,
    payload,
    context,
    uscEnv,
});

export const fetchUsedFinanceQuote = (
    payload: UsedFinanceInsurancePayload,
    context: UscContext,
): FetchUsedFinanceQuote => ({
    fetchType: FETCH_USED_FINANCE_QUOTE,
    payload,
    context,
});

export const fetchStockFinanceQuote = (payload: StockFinancePayload, context: UscContext): FetchStockFinanceQuote => ({
    fetchType: FETCH_STOCK_FINANCE_QUOTE,
    payload,
    context,
});

export const fetchUscInsuranceQuote = (quoteId: string, uscEnv: UscEnv): FetchUscInsuranceQuote => ({
    fetchType: FETCH_USC_INSURANCE_QUOTE,
    quoteId,
    uscEnv,
});

export const fetchEcoCosts = (payload: EcoCostsPayloadType, uscEnv: UscEnv): FetchEcoCostsType => ({
    fetchType: FETCH_ECO_COSTS,
    payload,
    uscEnv,
});

export type UscFetchTypes =
    | FetchStockFinanceQuote
    | FetchUscSettingsType
    | FetchUscVehicleType
    | FetchUscPreviewVehicleType
    | FetchUscTyCodeType
    | FetchUscDealerType
    | FetchExternalPriceType
    | FetchUsedFinanceQuote
    | FetchUscInsuranceQuote
    | FetchUscAccessoriesType
    | FetchUscVehiclePriceType
    | FetchEcoCostsType
    | FetchAggregatedVehicleCountType;

// ----------------------------------------------------------------------
// Fetch getter
// ----------------------------------------------------------------------
export const getUscComponentFetches = (state: UscStateType): UscFetchTypes[] => {
    const isSSR = !DOMUtils.canUseDOM;
    const fetches: UscFetchTypes[] = [];
    const { vehicle, dealer, save, externalPrice, finance, insurance, accessories, carDelivery, ecoCosts } = state;
    const { uscSettings, commonSettings } = state;
    const isPDF = commonSettings.component === COMPONENT_USED_STOCK_CARS_PDF;

    // Fetch the settings before fetching any other data.
    if (!uscSettings.initialized) {
        fetches.push(fetchUscSettings(vehicle.type));
        return fetches;
    }

    // Current clientside fetch logic is based on vehicle.fetchClientSide.
    // Consider splitting this into somewhere central if it gets too complicated, as finance/insurance/save fetching is also impacted by this.
    if (!vehicle.result) {
        const fetchVehicleData = (vehicle.fetchClientSide && !isSSR) || (isSSR && !vehicle.fetchClientSide) || isPDF;

        // fetchClientSide will be true if the initial server side fetch was not cached.
        // Make sure we don't end up in an infinite fetch loop by also checking for fetchFailed.
        if (fetchVehicleData && !vehicle.fetchFailed) {
            // Only wait for uncached vehicle if the request is client side or if we're rendering a PDF/AEM versions.
            // If the vehicle is uncached the SSR will be too slow for Tridion.
            // Note that disabling the wait for uncached vehicle logic for AEM causes an issue with the image slider not initialising properly, see OR-5683

            // Temporarily setting this to true by default to fix SSR rendering, should be fixed by deprecating waitForUncachedResult entirely,
            // refer to UC-509.
            const waitForUncachedResult = true;

            // We should always have a vehicleForSaleId, even for a saved car as the URLs contain the ID.
            // If the id was in query/body it should be in the vehicle reducer as this should have been parsed in preFetch.
            const vehicleForSaleId = save.data?.vehicleForSaleId || vehicle.vehicleForSaleId || "";

            // If this is an author preview we don't have a vehicleForSaleId. Use the preview fetcher instead.
            if (commonSettings.query.authorPreview) {
                fetches.push(fetchUscPreviewVehicle(vehicle.type, uscSettings.uscEnv));
            } else if (vehicleForSaleId) {
                const overrides: VehicleOverridesType = {};
                if (commonSettings.query.vehicleStatus) {
                    overrides.status = vehicleStatusQueryMap[commonSettings.query.vehicleStatus];
                }
                if (commonSettings.query.isHubCar) overrides.isHubCar = commonSettings.query.isHubCar;

                fetches.push(
                    fetchUscVehicle(
                        vehicleForSaleId,
                        vehicle.type,
                        uscSettings.uscEnv,
                        waitForUncachedResult,
                        commonSettings.query.loadVehicleSourceData,
                        overrides,
                    ),
                );
            }

            const tyCode = getToyotaCode(commonSettings.query);
            if (tyCode) {
                fetches.push(fetchUscTyCode(tyCode, uscSettings.loadSaveGatewayUrl, waitForUncachedResult));
            }
        }
    }

    // Fetch accessories
    if (uscSettings.enableAccessories && vehicle.result && !accessories.initialized && !accessories.fetchFailed) {
        fetches.push(fetchUscAccessories(getUscAccessoryPackParams(vehicle.result, uscSettings.uscEnv)));
    }

    // Load USC dealer if necessary
    if (vehicle.result && vehicle.result.dealerId && !vehicle.result.isHubCar && !dealer.initialized) {
        fetches.push(fetchUscDealer(vehicle.result.dealerId));
    }

    // if forceClientRefresh is present, and we're not SSR -> fetch the price (client-side, after vehicle init).
    if (!isSSR && vehicle.result && vehicle.result.price?.forceClientRefresh) {
        fetches.push(fetchUscVehiclePrice(vehicle.vehicleForSaleId, vehicle.type, uscSettings.uscEnv));
    }

    if (
        enableLegalEcoLabel(commonSettings.country, vehicle.type, uscSettings) &&
        !ecoCosts.initialized &&
        !ecoCosts.failed
    ) {
        const payload = getEcoCostsPayload(vehicle.result);

        if (!payload) return fetches;

        fetches.push(fetchEcoCosts(payload, uscSettings.uscEnv));
    }

    if (
        uscSettings.enableVehicleAggregations &&
        uscSettings.vehicleAggregationsVersionCode &&
        vehicle.result &&
        // Normally we make use of initialized flags to indicate if we already tried fetching data.
        // As vehicleCount is a property of a result object, we can't use this flag (the result object is the one that is initialized).
        // By default, vehicleCount will be undefined. If it's null, we've already tried fetching the data (and failed).
        vehicle.result.vehicleCount === undefined &&
        vehicle.result.vehicleCount !== null
    ) {
        fetches.push(
            fetchAggregatedVehicleCount(uscSettings.vehicleAggregationsVersionCode, vehicle.type, uscSettings.uscEnv),
        );
    }

    if (!isPDF && !commonSettings.query.authorPreview) return fetches;

    // ----------------------------------------------------------------------
    // PDF/Author preview fetches
    //
    // These fetches usually have a frontend equivalent which happens client side.
    // ----------------------------------------------------------------------

    // TMEF logic for PDF fetch. This used to be frontend logic but frontend now implements the function directly. See useUscExternalPriceInit hook.
    // Load TMEF data when enabled in settings. If a dealer ID is present wait until the dealer is fetched.
    // Note that we always load TMEF data if enabled in settings, but the logic which determines if we actually show this data is in the externalPrice reducer.
    const { enableExternalPrice } = uscSettings;
    const loadTmefData =
        showUscPriceSelector(state) && enableExternalPrice && !externalPrice.data && !externalPrice.failed;

    if (vehicle.result && loadTmefData && (!vehicle.result.dealerId || dealer.initialized)) {
        fetches.push(
            fetchTmefData(
                getExternalPricePayload(
                    vehicle.type,
                    vehicle.result,
                    dealer.dealer,
                    externalPrice.userTaxLocationId,
                    accessories.accessoryResults,
                    accessories.packResults,
                    carDelivery.deliveryConfig,
                    commonSettings.brand,
                ),
                vehicle.type,
                uscSettings.uscEnv,
            ),
        );
    }

    // Load finance data.
    // Also make sure TMEF data is available before fetching.
    if (vehicle.result && (!enableExternalPrice || externalPrice.data || externalPrice.failed)) {
        const enableMonthly = enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type);

        if (enableMonthly && !finance.info && !finance.failed) {
            const payload = getFinanceInsurancePayloadSelector("finance")(state);
            if (payload)
                fetches.push(
                    vehicle.type === UscContext.Used
                        ? fetchUsedFinanceQuote(payload as UsedFinanceInsurancePayload, vehicle.type)
                        : fetchStockFinanceQuote(payload as StockFinancePayload, vehicle.type),
                );
        }
    }

    // Insurance fetching is only required when an insuranceQuoteId is present.
    if (vehicle.result && insurance.insuranceQuoteId) {
        const enableInsurance = enableUscInsurance(commonSettings, uscSettings, vehicle.result, vehicle.type);

        if (enableInsurance && !insurance.failed && !insurance.quote) {
            fetches.push(fetchUscInsuranceQuote(insurance.insuranceQuoteId, uscSettings.uscEnv));
        }
    }

    return fetches;
};

// ----------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------
export const uscFetchDispatcher = (data: Required<UscFetchTypes>, dispatch: UscDispatchType): void => {
    const isSSR = !DOMUtils.canUseDOM;

    switch (data.fetchType) {
        case FETCH_USC_SETTINGS: {
            if (data.result) {
                dispatch(initUscSettings(data.result.fetchResult));
            }
            break;
        }
        case FETCH_USC_VEHICLE: {
            if (data.result) {
                const { fetchResult } = data.result;

                if (!fetchResult) {
                    // Result object is present without a fetchResult. Most likely an error with errorLogs, set failed state.
                    dispatch(setVehicleFetchFailed(true));
                    return;
                }

                if (data.overrides.status) fetchResult.vehicle.vehicleStatus.code = data.overrides.status;
                if (data.overrides.isHubCar) fetchResult.vehicle.isHubCar = data.overrides.isHubCar;

                // When the PDP is SSR'd, we don't want to render the price as this will be cached indefinitely.
                // In these cases, we set a flag on the price object to indicate that it should be fetched client side.
                if (isSSR && fetchResult.vehicle.price) {
                    fetchResult.vehicle.price.forceClientRefresh = true;
                }

                dispatch(initVehicle(fetchResult.vehicle, fetchResult.source || null));
                if (fetchResult.source) dispatch(showModal({ type: MODAL_USC_DEBUG }));
            } else if (isSSR) {
                dispatch(setRequireVehicleFetch(true));
            } else {
                // This case should be rare as most fails should be covered in errorLogs.
                dispatch(setVehicleFetchFailed(true));
            }
            break;
        }

        case FETCH_USC_VEHICLE_PRICE: {
            if (data.result) {
                dispatch(setVehiclePrice(data.result.fetchResult));
            } else {
                dispatch(setVehicleFetchFailed(true));
            }
            break;
        }

        case FETCH_USC_ACCESSORIES: {
            if (data.result) {
                const { fetchResult } = data.result;
                if (!fetchResult) dispatch(setUscAccessoriesFailed(true));
                else dispatch(initUscAccessories(fetchResult.accessories, fetchResult.packs, fetchResult.esQuery));
            } else {
                dispatch(setUscAccessoriesFailed(true));
            }
            break;
        }

        case FETCH_USC_PREVIEW_VEHICLE: {
            // Based on FETCH_USC_VEHICLE.
            if (data.result) {
                const { fetchResult } = data.result;

                if (!fetchResult) {
                    dispatch(setVehicleFetchFailed(true));
                    return;
                }
                dispatch(initVehicle(fetchResult.vehicle, fetchResult.source || null));
            } else {
                dispatch(setVehicleFetchFailed(true));
            }
            break;
        }

        case FETCH_USC_TYCODE: {
            if (data.result) {
                const { fetchResult } = data.result;

                if (!fetchResult) {
                    // Result is present without a fetchResult. Most likely an error with errorLogs, set failed state.
                    dispatch(setVehicleFetchFailed(true));
                    return;
                }

                dispatch(setCode(fetchResult));
                if (fetchResult.insuranceQuoteId) dispatch(setInsuranceQuoteId(fetchResult.insuranceQuoteId));
                if (fetchResult.financeQuoteId) dispatch(setFinanceQuoteId(fetchResult.financeQuoteId));
                if (fetchResult.userDepartmentCode) dispatch(setUserTaxLocation(fetchResult.userDepartmentCode));
                if (fetchResult.carDelivery) dispatch(setCarDeliveryConfig(fetchResult.carDelivery));
            } else if (!isSSR) {
                dispatch(setVehicleFetchFailed(true));
            }
            break;
        }

        case FETCH_USC_DEALER: {
            if (data.result?.fetchResult) {
                const { fetchResult } = data.result;
                dispatch(initDealer(fetchResult));
            } else {
                Debug.warn(`Cannot find dealer with id ${data.dealerId}`);
                dispatch(initDealer(undefined, true));
            }
            break;
        }

        case FETCH_EXT_PRICE_DATA: {
            if (data.result?.fetchResult.failed) {
                dispatch(setExternalPriceFailed(true, data.payload, data.result.fetchResult.errorMessage));
            } else if (data.result?.fetchResult.data) {
                dispatch(setExternalPriceData(data.result.fetchResult.data, data.payload));
            }
            break;
        }

        case FETCH_USED_FINANCE_QUOTE:
        case FETCH_STOCK_FINANCE_QUOTE: {
            if (data.result?.fetchResult) {
                const { fetchResult } = data.result;
                dispatch(
                    setFinanceInfo(
                        fetchResult.financeInfo,
                        fetchResult.monthlyPrice,
                        data.payload,
                        {
                            source:
                                data.fetchType === FETCH_USED_FINANCE_QUOTE
                                    ? UscFinanceDataSource.ApiUsed
                                    : UscFinanceDataSource.ApiStock,
                            data: fetchResult.response,
                        },
                        data.payload.parameters?.financeQuoteId || null,
                        fetchResult.accessories,
                        fetchResult.services,
                        fetchResult.costs,
                    ),
                );
            } else {
                dispatch(setFinanceFetchFailed(true, data.payload));
            }
            break;
        }

        case FETCH_USC_INSURANCE_QUOTE: {
            if (data.result?.fetchResult) {
                dispatch(setInsuranceQuote(data.result.fetchResult));
            } else {
                dispatch(setInsuranceFailed(true));
            }
            break;
        }

        case FETCH_ECO_COSTS: {
            if (data?.result?.fetchResult) {
                const { fetchResult } = data.result;

                if (fetchResult.errors.length === 0) {
                    dispatch(setEcoCostsData(fetchResult, data.payload));
                } else {
                    dispatch(setEcoCostsErrors(fetchResult.errors));
                    dispatch(setEcoCostsFailed(true, data.payload));
                }
            } else {
                dispatch(setEcoCostsFailed(true, data.payload));
            }
            break;
        }

        case FETCH_USC_AGGREGATED_VEHICLE_COUNT: {
            if (data.result?.fetchResult) {
                const { fetchResult } = data.result;

                dispatch(setAggregatedVehicleCount(fetchResult.count));
            }

            break;
        }
    }
};
